import type { RenamePair } from '@store/services/questionnaire/types';

export const applySanitizedRenames = (
  renamesKey: string,
  newValues: unknown[],
  getValues: (key: string) => unknown,
  setValue: (key: string, value: unknown, opts?: object) => void,
): void => {
  const normalizedValues = newValues
    .map((v) => (typeof v === 'string' ? v.trim() : ''))
    .filter(Boolean);
  const current = (getValues(renamesKey) ?? []) as RenamePair[];
  const sanitized = sanitizeRenamesByNewNames(current, normalizedValues);
  if (sanitized.length !== current.length) {
    setValue(renamesKey, sanitized, { shouldDirty: true });
  }
};

export const sanitizeRenamesByNewNames = (
  renames: RenamePair[],
  normalizedNewNames: string[],
): RenamePair[] =>
  renames.filter(
    (rename) =>
      typeof rename.old_name === 'string' &&
      typeof rename.new_name === 'string' &&
      normalizedNewNames.includes(rename.new_name),
  );

export const normalizeAnswersToStrings = (answers: unknown[] = []): string[] =>
  answers
    .map((answer) => {
      if (!answer) return '';
      if (typeof answer === 'string') return answer;

      if (typeof answer === 'object') {
        const typed = answer as Partial<RenamePair> & {
          label?: unknown;
          value?: unknown;
        };

        const candidate =
          typed.new_name ??
          typed.old_name ??
          (typeof typed.value === 'string' ? typed.value : null) ??
          (typeof typed.label === 'string' ? typed.label : null);

        return typeof candidate === 'string' ? candidate : '';
      }

      return '';
    })
    .filter(Boolean);

export const computeCurrIndexToPrevIndex = (
  previous: string[],
  current: string[],
): (number | null)[] => {
  const m = previous.length;
  const n = current.length;
  const dp: number[][] = Array.from({ length: m + 1 }, () =>
    Array(n + 1).fill(0),
  );

  for (let i = 1; i <= m; i += 1) {
    for (let j = 1; j <= n; j += 1) {
      if (previous[i - 1] === current[j - 1]) {
        dp[i][j] = dp[i - 1][j - 1] + 1;
      } else {
        dp[i][j] = Math.max(dp[i - 1][j], dp[i][j - 1]);
      }
    }
  }

  const map: (number | null)[] = Array(n).fill(null);
  let i = m;
  let j = n;

  while (i > 0 && j > 0) {
    if (previous[i - 1] === current[j - 1]) {
      map[j - 1] = i - 1;
      i -= 1;
      j -= 1;
    } else if (dp[i - 1][j] >= dp[i][j - 1]) {
      i -= 1;
    } else {
      j -= 1;
    }
  }

  return map;
};

export const mapRenamePairsForUpdate = (
  currentAnswers: unknown[] = [],
  previousAnswers: unknown[] = [],
  answerRenames: RenamePair[] = [],
): RenamePair[] => {
  const C = normalizeAnswersToStrings(currentAnswers);
  const P = normalizeAnswersToStrings(previousAnswers);

  if (P.length === 0) {
    return C.map((name) => ({
      old_name: null,
      new_name: name,
    }));
  }

  const currToPrev = computeCurrIndexToPrevIndex(P, C);
  const matchedPrevIndices = new Set(
    currToPrev.filter((idx): idx is number => idx !== null),
  );
  const consumedRenamePrevIndices = new Set<number>();
  const renameEntries = answerRenames.filter(
    (rename): rename is { old_name: string; new_name: string } =>
      typeof rename.old_name === 'string' &&
      typeof rename.new_name === 'string',
  );

  return C.map((currentName, j) => {
    const prevIdx = currToPrev[j];

    if (prevIdx !== null) {
      return {
        old_name: P[prevIdx],
        new_name: null,
      };
    }

    const rename = renameEntries.find(
      (entry) => entry.new_name === currentName,
    );

    if (rename) {
      const oldIdx = P.findIndex(
        (answer, idx) =>
          answer === rename.old_name &&
          !matchedPrevIndices.has(idx) &&
          !consumedRenamePrevIndices.has(idx),
      );

      if (oldIdx !== -1) {
        consumedRenamePrevIndices.add(oldIdx);
        return {
          old_name: P[oldIdx],
          new_name: currentName,
        };
      }
    }

    return {
      old_name: null,
      new_name: currentName,
    };
  }).filter(
    ({ old_name, new_name }) => !(old_name === null && new_name === null),
  );
};
